import { setAuthSessionItem, clearAuthSession } from "utils/authSession";
import RctiEligibility from "components/RctiEligibility";
import ThemedSelect from "components/FormControls/ThemedSelect";
import "layouts/RCTI/agreements.css";
/* eslint-disable react-hooks/exhaustive-deps */
import { Modal } from "@mui/material";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { userlogin } from "ReduxReducers/loginReducers";
import brandLogo from "assets/images/auth/brand-logo.svg";
import getCroppedImg from "layouts/profile/editMyProfile/cropImage";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Cropper from "react-easy-crop";
import {
  LuBuilding2,
  LuCheck,
  LuImage,
  LuInfo,
  LuLoader,
  LuUserRound,
  LuX,
} from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import { wizardSignup } from "utils/authApi";
import validateSignupIdentity from "utils/signupIdentityValidation";
import SignupAbrLookup from "components/SignupAbrLookup";
import { getSesstionDataByUserID, showResMsg } from "utils/generic";
import "./SignUp.scss";

const initialValues = {
  companyName: "",
  organizationType: "Individual",
  abnNumber: "",
  acnNumber: "",
  companyStreetAddress: "",
  companyCity: "",
  companyStateId: "",
  companyCountryId: "",
  companyZipCode: "",
  industryType: "Professional, scientific and technical services",
  accountName: "",
  bsb: "",
  accountNumber: "",
  confirmAccountNumber: "",
};

const steps = [
  ["Business details", "Business identity and type"],
  ["Business address", "Business identity and address"],
  ["Banking details", "Optional · Add payment details"],
];

function Field({ label, required, error, hint, children }) {
  return (
    <label className="business-setup-field">
      <span className="business-setup-field-label">
        {label}
        {required && <b className="required-marker"> *</b>}
      </span>
      {children}
      {error && <small className="business-setup-error">{error}</small>}
      {!error && hint && <small className="business-setup-hint">{hint}</small>}
    </label>
  );
}

function Cover() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const countryData =
    useSelector((store) => store.countryState?.countryStateMenu?.country) || [];
  const stateData =
    useSelector((store) => store.countryState?.countryStateMenu?.state) || [];
  const [step, setStep] = useState(1);
  const [values, setValues] = useState({ ...initialValues, id });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [actualFile, setActualFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [cropSource, setCropSource] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const australia = useMemo(
    () => countryData.find((item) => /australia/i.test(item.countryName || item.name || "")),
    [countryData]
  );
  const availableStates = useMemo(
    () =>
      !australia
        ? stateData
        : stateData.filter((item) => {
            const countryId = item.countryId?._id || item.countryId || item.countryID;
            return !countryId || String(countryId) === String(australia._id);
          }),
    [australia, stateData]
  );

  const stateOptions = useMemo(
    () =>
      availableStates.map((item) => ({
        value: String(item._id),
        label: item.stateName || item.name,
        raw: item,
      })),
    [availableStates]
  );

  useEffect(() => {
    dispatch(loadingAction(false));
    const stored = sessionStorage.getItem("SIGN_UP_TEMP_DATA");
    if (stored) {
      try {
        setValues((current) => ({ ...current, ...JSON.parse(stored), id }));
      } catch (error) {
        /* Invalid legacy data is ignored. */
      }
      sessionStorage.removeItem("SIGN_UP_TEMP_DATA");
    }
  }, [id]);

  useEffect(() => {
    if (australia?._id && !values.companyCountryId) {
      setValues((current) => ({
        ...current,
        companyCountryId: australia._id,
        companyCountryName: australia.countryName || australia.name || "Australia",
      }));
    }
  }, [australia, values.companyCountryId]);

  const update = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const formatAbn = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    return [digits.slice(0, 2), digits.slice(2, 5), digits.slice(5, 8), digits.slice(8, 11)]
      .filter(Boolean)
      .join(" ");
  };

  const validateStep = (number) => {
    const next = {};
    if (number === 1) {
      if (!values.companyName.trim()) next.companyName = "Enter your business or legal name.";
      if (!values.organizationType) next.organizationType = "Choose an organisation type.";
      Object.assign(next, validateSignupIdentity(values));
    }
    if (number === 2) {
      if (!values.companyStreetAddress.trim()) next.companyStreetAddress = "Enter your street address.";
      if (!values.companyCity.trim()) next.companyCity = "Enter your suburb or city.";
      if (!values.companyStateId) next.companyStateId = "Choose your state or territory.";
      if (!/^\d{4}$/.test(values.companyZipCode)) next.companyZipCode = "Enter a valid 4-digit postcode.";
    }
    if (number === 3 && values.accountNumber && values.accountNumber !== values.confirmAccountNumber) {
      next.confirmAccountNumber = "Account numbers do not match.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep((current) => Math.min(3, current + 1));
  };

  const onCropComplete = useCallback((unused, pixels) => setCroppedAreaPixels(pixels), []);

  const chooseLogo = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    if (selected.size > 1024 * 1024) {
      showResMsg("File can't be more than 1 MB", 2000, false);
      return;
    }
    if (!/image\/(png|jpe?g|svg\+xml)/i.test(selected.type)) {
      showResMsg("Choose a PNG, JPG or SVG image", 2000, false);
      return;
    }
    setActualFile(selected);
    setCropSource(URL.createObjectURL(selected));
  };

  const saveCrop = async () => {
    try {
      const cropped = await getCroppedImg(cropSource, croppedAreaPixels);
      const blob = await fetch(cropped).then((response) => response.blob());
      const croppedFile = new File([blob], actualFile.name, {
        type: blob.type || actualFile.type,
      });
      setActualFile(croppedFile);
      setLogoPreview(cropped);
      setCropSource("");
      setZoom(1);
    } catch (error) {
      showResMsg("We couldn't crop this image. Please try another file.", 2000, false);
    }
  };

  const finishSetup = async (skipBanking = false) => {
    if (!skipBanking && !validateStep(3)) return;
    setSubmitting(true);
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (!skipBanking || !["accountName", "bsb", "accountNumber", "confirmAccountNumber"].includes(key)) {
        formData.append(key, value ?? "");
      }
    });
    if (values.organizationType !== "Business") formData.delete("acnNumber");
    if (!skipBanking) {
      formData.set("bankAccountName", values.accountName);
      formData.set("bankBsbCode", values.bsb.replace(/\D/g, ""));
      formData.set("bankAccountNumber", values.accountNumber);
    }
    if (actualFile) {
      formData.append("file", actualFile);
      formData.append("fileName", actualFile.name);
    }
    wizardSignup(
      formData,
      async (data) => {
        if (!data.Result_Status) {
          setSubmitting(false);
          dispatch(loadingAction(false));
          showResMsg(data.Result_Message, 2000, false);
          return;
        }
        const userId = data.Result_Output?.data?._id || id;
        const ready = await getSesstionDataByUserID(userId, dispatch);
        if (!ready) {
          clearAuthSession();
          navigate("/login", { replace: true });
          return;
        }
        dispatch(userlogin(JSON.parse(sessionStorage.getItem("LOGIN_DETAILS"))));
        setAuthSessionItem("TOKEN", data.Result_Output.token);
        swal({
          title: "Welcome to Invoice & Recover!",
          text: "Your business is ready.",
          icon: "success",
          buttons: false,
          timer: 1600,
        });
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 1600);
      },
      dispatch
    );
  };

  return (
    <main className="business-setup-page">
      <aside className="business-setup-brand">
        <span className="business-setup-orb business-setup-orb--one" />
        <span className="business-setup-orb business-setup-orb--two" />
        <span className="business-setup-orb business-setup-orb--three" />
        <div className="business-setup-brand-content">
          <img src={brandLogo} alt="Invoice & Recover" className="business-setup-brand-logo" />
          <div className="business-setup-brand-copy">
            <h1>Let’s get your business ready.</h1>
            <p>Set up your business so you’re ready to invoice and get paid.</p>
          </div>
          <ol className="business-setup-progress">
            {steps.map(([title, copy], index) => (
              <li className={step === index + 1 ? "is-active" : ""} key={title}>
                <span>{index + 1}</span>
                <div>
                  <strong>{title}</strong>
                  <small>{copy}</small>
                </div>
              </li>
            ))}
          </ol>
          <small className="business-setup-secure">
            Secure setup · You can update these details anytime.
          </small>
        </div>
      </aside>

      <section className="business-setup-panel">
        <form className="business-setup-form" onSubmit={(event) => event.preventDefault()}>
          <div className="business-setup-content">
            <header className="business-setup-heading">
              <div className="business-setup-step-row">
                <span>STEP {step} OF 3</span>
                {step === 3 && (
                  <button
                    type="button"
                    className="business-setup-skip-btn"
                    onClick={() => finishSetup(true)}
                    disabled={submitting}
                  >
                    Skip for now
                  </button>
                )}
              </div>
              <h2>
                {step === 1
                  ? "Add your business details"
                  : step === 2
                  ? "Add your business address"
                  : "Add your banking details"}
              </h2>
              <p>
                {step === 1
                  ? "We'll use these details on your invoices and quotes."
                  : step === 2
                  ? "Add your business location for your business records and documents."
                  : "These details can be used to show payment information on your invoices. You can add or update them later in Settings."}
              </p>
            </header>

            {step === 1 && (
              <div className="business-setup-fields">
                <div className="business-logo-upload">
                  <span className="business-logo-avatar">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Business logo preview" />
                    ) : (
                      <LuImage />
                    )}
                  </span>
                  <div className="business-logo-info">
                    <div className="business-logo-title-row">
                      <strong>Upload your business logo</strong>
                      <span className="business-logo-optional-tag">Optional</span>
                    </div>
                    <small>PNG, JPG or SVG · Add it later in Settings</small>
                  </div>
                  <button
                    type="button"
                    className="business-logo-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={chooseLogo}
                    hidden
                  />
                </div>

                <Field label="Business or legal name" required error={errors.companyName}>
                  <input
                    className="business-setup-input"
                    value={values.companyName}
                    onChange={(event) => update("companyName", event.target.value)}
                    placeholder="e.g. Maple Electrical"
                    maxLength={80}
                  />
                </Field>

                <fieldset className="business-type-field">
                  <legend>
                    Organisation type <b className="required-marker">*</b>
                  </legend>
                  <div className="business-type-cards">
                    {[
                      ["Individual", LuUserRound],
                      ["Business", LuBuilding2],
                    ].map(([type, Icon]) => (
                      <button
                        className={`business-type-card${
                          values.organizationType === type ? " is-selected" : ""
                        }`}
                        type="button"
                        key={type}
                        onClick={() => update("organizationType", type)}
                      >
                        <span className="business-type-icon-box">
                          <Icon />
                        </span>
                        <span className="business-type-title">{type}</span>
                        <i className="business-type-check-badge">
                          {values.organizationType === type && <LuCheck />}
                        </i>
                      </button>
                    ))}
                  </div>
                  {errors.organizationType && (
                    <small className="business-setup-error">{errors.organizationType}</small>
                  )}
                </fieldset>

                <Field
                  label={values.organizationType === "Business" ? "Australian Business Number (ABN)" : "Australian Business Number (ABN) (optional)"}
                  required={values.organizationType === "Business"}
                  error={errors.abnNumber}
                  hint={values.organizationType === "Business" ? "Enter your 11-digit business ABN." : "Add your 11-digit ABN if you have one."}
                >
                  <input
                    className="business-setup-input"
                    value={values.abnNumber}
                    onChange={(event) => update("abnNumber", formatAbn(event.target.value))}
                    placeholder="e.g. 12 345 678 901"
                    inputMode="numeric"
                  />
                </Field>
                {values.organizationType === "Business" && (
                  <Field
                    label="Australian Company Number (ACN)"
                    required
                    error={errors.acnNumber}
                    hint="Enter your 9-digit company ACN."
                  >
                    <input
                      className="business-setup-input"
                      value={values.acnNumber}
                      onChange={(event) => update("acnNumber", event.target.value.replace(/\D/g, "").slice(0, 9).replace(/(\d{3})(?=\d)/g, "$1 "))}
                      placeholder="e.g. 123 456 789"
                      inputMode="numeric"
                    />
                  </Field>
                )}
                <RctiEligibility values={values} onChange={update} />
                <SignupAbrLookup
                  companyName={values.companyName}
                  abn={values.abnNumber}
                  organizationType={values.organizationType}
                  onApply={(business) => {
                    const acn = String(business.acn || "").replace(/\D/g, "");
                    setValues((current) => ({
                      ...current,
                      companyName: business.companyName,
                      abnNumber: formatAbn(business.abn),
                      ...(current.organizationType === "Business" && acn.length === 9
                        ? { acnNumber: acn.replace(/(\d{3})(?=\d)/g, "$1 ") }
                        : {}),
                    }));
                    setErrors((current) => ({ ...current, companyName: "", abnNumber: "", ...(acn.length === 9 ? { acnNumber: "" } : {}) }));
                  }}
                />
              </div>
            )}

            {step === 2 && (
              <div className="business-setup-fields">
                <Field label="Street address" required error={errors.companyStreetAddress}>
                  <input
                    className="business-setup-input"
                    value={values.companyStreetAddress}
                    onChange={(event) => update("companyStreetAddress", event.target.value)}
                    placeholder="Street and number"
                  />
                </Field>

                <Field label="Suburb / city" required error={errors.companyCity}>
                  <input
                    className="business-setup-input"
                    value={values.companyCity}
                    onChange={(event) => update("companyCity", event.target.value)}
                    placeholder="Suburb or city"
                  />
                </Field>

                <div className="business-setup-field-row">
                  <Field label="State or territory" required>
                    <ThemedSelect
                      id="company-state-select"
                      className="business-setup-themed-select"
                      value={values.companyStateId}
                      aria-label="State or territory"
                      errorMessage={errors.companyStateId}
                      onChange={(event) => {
                        const val = event.target.value;
                        const opt = stateOptions.find(
                          (option) => String(option.value) === String(val)
                        );
                        setValues((current) => ({
                          ...current,
                          companyStateId: val,
                          companyStateName: opt?.label || "",
                        }));
                        setErrors((current) => ({ ...current, companyStateId: "" }));
                      }}
                    >
                      <option value="">Choose state or territory</option>
                      {stateOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </ThemedSelect>
                  </Field>

                  <Field label="Postcode" required error={errors.companyZipCode}>
                    <input
                      className="business-setup-input"
                      value={values.companyZipCode}
                      onChange={(event) =>
                        update("companyZipCode", event.target.value.replace(/\D/g, "").slice(0, 4))
                      }
                      placeholder="e.g. 2000"
                      inputMode="numeric"
                    />
                  </Field>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="business-setup-fields">
                <div className="business-setup-notice">
                  <LuInfo className="notice-icon" />
                  <div>
                    <strong>This step is optional</strong>
                    <span>
                      Prefer to finish setup first? Skip this step and add your bank details later.
                    </span>
                  </div>
                </div>

                <Field
                  label="Account name"
                  hint="Enter the account name exactly as registered with your bank."
                >
                  <input
                    className="business-setup-input"
                    value={values.accountName}
                    onChange={(event) => update("accountName", event.target.value)}
                    placeholder="e.g. Maple Electrical"
                  />
                </Field>

                <div className="business-setup-field-row">
                  <Field label="BSB">
                    <input
                      className="business-setup-input"
                      value={values.bsb}
                      onChange={(event) =>
                        update(
                          "bsb",
                          event.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6)
                            .replace(/(\d{3})(?=\d)/, "$1-")
                        )
                      }
                      placeholder="000-000"
                      inputMode="numeric"
                    />
                  </Field>

                  <Field label="Account number">
                    <input
                      className="business-setup-input"
                      value={values.accountNumber}
                      onChange={(event) =>
                        update("accountNumber", event.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="Account number"
                      inputMode="numeric"
                    />
                  </Field>
                </div>

                <Field label="Confirm account number" error={errors.confirmAccountNumber}>
                  <input
                    className="business-setup-input"
                    value={values.confirmAccountNumber}
                    onChange={(event) =>
                      update(
                        "confirmAccountNumber",
                        event.target.value.replace(/\D/g, "").slice(0, 10)
                      )
                    }
                    placeholder="Enter account number again"
                    inputMode="numeric"
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="business-setup-actions">
            {step > 1 && (
              <button
                className="business-setup-button business-setup-button--secondary"
                type="button"
                onClick={() => setStep((current) => current - 1)}
              >
                Back
              </button>
            )}
            <button
              className="business-setup-button business-setup-button--primary"
              type="button"
              disabled={submitting}
              onClick={step < 3 ? nextStep : () => finishSetup(false)}
            >
              {submitting ? (
                <>
                  <LuLoader className="business-btn-spin" />
                  <span>Saving…</span>
                </>
              ) : step < 3 ? (
                "Continue"
              ) : (
                "Finish setup"
              )}
            </button>
          </div>
        </form>

        <footer className="business-setup-footer">
          <a href="https://edios.global/our-solutions/" target="_blank" rel="noreferrer">
            Privacy
          </a>
          <span>·</span>
          <a href="https://edios.global/our-solutions/" target="_blank" rel="noreferrer">
            Terms
          </a>
        </footer>
      </section>

      {/* Crop Logo Modal */}
      <Modal
        open={Boolean(cropSource)}
        onClose={() => setCropSource("")}
        aria-labelledby="business-crop-modal-title"
      >
        <div className="business-crop-modal" role="dialog" aria-modal="true">
          <div className="business-crop-header">
            <h3 id="business-crop-modal-title" className="business-crop-title">
              Crop your business logo
            </h3>
            <button
              type="button"
              className="business-crop-close-btn"
              onClick={() => setCropSource("")}
              aria-label="Close"
            >
              <LuX />
            </button>
          </div>

          <div className="business-crop-area">
            <Cropper
              image={cropSource}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          <div className="business-crop-zoom-row">
            <label htmlFor="crop-zoom-slider" className="business-crop-zoom-label">
              Zoom
            </label>
            <input
              id="crop-zoom-slider"
              type="range"
              min="1"
              max="5"
              step="0.1"
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              className="business-crop-zoom-slider"
            />
          </div>

          <div className="business-crop-actions">
            <button
              type="button"
              className="business-crop-btn business-crop-btn--cancel"
              onClick={() => setCropSource("")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="business-crop-btn business-crop-btn--save"
              onClick={saveCrop}
            >
              <LuCheck /> Use logo
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
}

export default Cover;
